"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShareDocumentForm = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const BasicButton_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton");
const Checkbox_1 = require("@jsonjoy.com/ui/lib/2-inline-block/Checkbox");
const Input_1 = require("@jsonjoy.com/ui/lib/2-inline-block/Input");
const TextBlock_1 = require("@jsonjoy.com/ui/lib/5-block/TextBlock");
const FormRow_1 = require("@jsonjoy.com/ui/lib/3-list-item/FormRow");
const Separator_1 = require("@jsonjoy.com/ui/lib/3-list-item/Separator");
const Iconista_1 = require("@jsonjoy.com/ui/lib/icons/Iconista");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const context_2 = require("../context");
const shareEncoding_1 = require("../util/shareEncoding");
const share_1 = require("../util/share");
const use_t_1 = require("use-t");
const Split_1 = require("@jsonjoy.com/ui/lib/3-list-item/Split");
const Space_1 = require("@jsonjoy.com/ui/lib/3-list-item/Space");
const lock_1 = tslib_1.__importDefault(require("iconista/lib/react/tabler/lock"));
const LockIcon = (props) => (React.createElement(lock_1.default, { width: 16, height: 16, ...props }));
const stopKeyDown = (e) => {
    e.stopPropagation();
};
// Container that hard-caps the popup width so a long URL inside the link
// preview can never push the popup wider mid-session.
const formClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    // gap: '12px',
    w: '100%',
    pd: '4px 16px 8px',
    bxz: 'border-box',
    // Critical: in a flex parent the auto min-width is min-content; without
    // this, a long single-line URL would force the column to grow.
    minW: 0,
});
const passwordRowClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    gap: '6px',
});
const socialRowClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'row',
    gap: '8px',
    flexWrap: 'wrap',
});
const sectionDescriptionClass = (0, nano_theme_1.rule)({
    fz: '12px',
    lh: 1.45,
    mr: '0 0 10px',
});
const errorClass = (0, nano_theme_1.rule)({
    fz: '12px',
    lh: 1.4,
    pdt: '4px',
});
const messageInputClass = (0, nano_theme_1.rule)({
    '& textarea': {
        resize: 'none',
    },
});
const SOCIAL_TARGETS = [
    {
        id: 'x',
        label: 'X',
        icon: 'brand-x',
        href: (url, text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    },
    {
        id: 'facebook',
        label: 'Facebook',
        icon: 'brand-facebook',
        href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
        id: 'linkedin',
        label: 'LinkedIn',
        icon: 'brand-linkedin',
        href: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
        id: 'reddit',
        label: 'Reddit',
        icon: 'brand-reddit',
        href: (url, text) => `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`,
    },
];
const formatBytes = (n) => {
    if (n < 1024)
        return `${n} B`;
    if (n < 1024 * 1024)
        return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const HTML_ESCAPES = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};
const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
const DEFAULT_TITLE = 'Shared MuTxt document';
const getDefaultTitle = (mutxt) => {
    try {
        const outline = mutxt.outline();
        const first = outline.find((o) => o.title.trim());
        if (first)
            return first.title.trim();
    }
    catch { }
    return DEFAULT_TITLE;
};
const ShareDocumentForm = () => {
    const [t] = (0, use_t_1.useT)();
    const mutxt = (0, context_2.useMuTxt)();
    const styles = (0, context_1.useStyles)();
    const [title, setTitle] = React.useState(() => getDefaultTitle(mutxt));
    const [message, setMessage] = React.useState('');
    const [encrypt, setEncrypt] = React.useState(false);
    const [password, setPassword] = React.useState('');
    const [shareUrl, setShareUrl] = React.useState('');
    const [encoding, setEncoding] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [htmlCopied, setHtmlCopied] = React.useState(false);
    const bytesRef = React.useRef(null);
    if (!bytesRef.current)
        bytesRef.current = mutxt.api.toBinary();
    const bytes = bytesRef.current;
    const effectiveTitle = title.trim() || DEFAULT_TITLE;
    const recomputeRef = React.useRef(0);
    const recompute = React.useCallback(async () => {
        const token = ++recomputeRef.current;
        setEncoding(true);
        setError(null);
        try {
            let encoded;
            if (encrypt) {
                if (!password) {
                    if (recomputeRef.current === token) {
                        setShareUrl('');
                        setEncoding(false);
                    }
                    return;
                }
                encoded = await (0, shareEncoding_1.encodeEncryptedShareBlob)(bytes, password);
            }
            else {
                encoded = await (0, shareEncoding_1.encodeShareBlob)(bytes);
            }
            if (recomputeRef.current !== token)
                return;
            setShareUrl((0, share_1.buildShareUrl)(encoded, encrypt, effectiveTitle, message));
        }
        catch (err) {
            if (recomputeRef.current !== token)
                return;
            setError(err instanceof Error ? err.message : 'Failed to encode the document.');
            setShareUrl('');
        }
        finally {
            if (recomputeRef.current === token)
                setEncoding(false);
        }
    }, [bytes, encrypt, password, effectiveTitle, message]);
    React.useEffect(() => {
        void recompute();
    }, [recompute]);
    const openSocial = (target) => {
        if (!shareUrl)
            return;
        const text = message.trim() || effectiveTitle;
        const href = target.href(shareUrl, text);
        window.open(href, '_blank', 'noopener,noreferrer');
    };
    const onCopyHtmlLink = async () => {
        if (!shareUrl)
            return;
        const html = `<a href="${escapeHtml(shareUrl)}">${escapeHtml(effectiveTitle)}</a>`;
        try {
            await (0, share_1.writeClipboard)(html);
            setHtmlCopied(true);
            setTimeout(() => setHtmlCopied(false), 2000);
            mutxt.toasts?.add({ title: 'HTML link copied to clipboard.', duration: 3000 });
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error('[mutxt] copy html link failed', err);
            mutxt.toasts?.add({ type: 'error', title: 'Failed to copy HTML link.', duration: 5000 });
        }
    };
    const errorColor = styles.col.get('error', 'solid-1');
    return (React.createElement("div", { className: formClass },
        React.createElement(FormRow_1.FormRow, { title: t('Display') },
            React.createElement(Input_1.Input, { type: "text", value: title, placeholder: t('Title'), onChange: setTitle, onKeyDown: stopKeyDown }),
            React.createElement(Space_1.Space, null),
            React.createElement("div", { className: messageInputClass },
                React.createElement(Input_1.Input, { type: "text", multiline: true, value: message, placeholder: t('Message'), onChange: setMessage, onKeyDown: stopKeyDown }))),
        React.createElement(FormRow_1.FormRow, { title: React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
                React.createElement(LockIcon, null),
                t('Encrypt')), descriptionAbove: true, description: t('Encrypts with AES-GCM 256 (PBKDF2).'), right: true },
            React.createElement("div", { style: { width: 60, marginTop: -24 } },
                React.createElement(Checkbox_1.Checkbox, { on: encrypt, onChange: () => setEncrypt(!encrypt) }))),
        encrypt && (React.createElement("div", { className: passwordRowClass },
            React.createElement(Input_1.Input, { type: "password", value: password, label: t('Password'), placeholder: t('Choose a strong password'), onChange: setPassword, onKeyDown: stopKeyDown }))),
        !!shareUrl && (React.createElement(React.Fragment, null,
            React.createElement(Space_1.Space, { size: 2 }),
            React.createElement(Separator_1.Separator, null),
            React.createElement(Space_1.Space, { size: 1 }))),
        !!shareUrl && (React.createElement(FormRow_1.FormRow, { title: React.createElement(Split_1.Split, null,
                React.createElement("div", null, t('Share link')),
                React.createElement("div", { className: sectionDescriptionClass, style: { color: styles.g(0.4) } }, encoding
                    ? t('Encoding…')
                    : shareUrl
                        ? `${formatBytes(shareUrl.length)} · ${encrypt ? t('Encrypted') : t('Plaintext')}`
                        : null)) }, error ? (React.createElement("div", { className: errorClass, style: { color: errorColor } }, error)) : (React.createElement("div", { style: { maxWidth: '100%' } },
            React.createElement(TextBlock_1.TextBlock, { src: shareUrl, select: true, lang: "text" }))))),
        shareUrl && (React.createElement(FormRow_1.FormRow, { title: t('Post to social media'), descriptionAbove: true, description: t("Opens the platform's share dialog.") },
            React.createElement("div", { className: socialRowClass },
                SOCIAL_TARGETS.map((t) => (React.createElement(BasicButton_1.BasicButton, { key: t.id, type: "button", width: 'auto', height: 32, compact: true, border: true, disabled: !shareUrl, onClick: () => openSocial(t) },
                    React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
                        React.createElement(Iconista_1.Iconista, { set: 'tabler', icon: t.icon, width: 16, height: 16 }))))),
                React.createElement(Separator_1.Separator, { style: { width: 1, height: 32 } }),
                React.createElement(BasicButton_1.BasicButton, { type: "button", width: 'auto', height: 32, compact: true, border: true, disabled: !shareUrl, onClick: onCopyHtmlLink },
                    React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
                        React.createElement(Iconista_1.Iconista, { set: 'tabler', icon: 'code', width: 16, height: 16 }),
                        htmlCopied ? t('Copied!') : t('Copy HTML'))))))));
};
exports.ShareDocumentForm = ShareDocumentForm;
